import { ACTIVITY_HISTORY_MIN_DAY } from "@common/constants/history/activity-history";
import { addCalendarDays, toIsoDate } from "@common/helpers/date";
import type { DateRangeFilterNextProps } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";

/** Without a known history start, the timeline reaches back at least this many days. */
const FALLBACK_HISTORY_DAYS = 30;
export const SINCE_LAST_VISIT_PRESET_ID = "since_last_visit";
export const SINCE_LAST_VISIT_LABEL = "Since your last visit";
/** The list's empty state when only the "Since your last visit" days are empty. */
export const SINCE_LAST_VISIT_EMPTY_STATE = {
    header: "No changes since your last visit",
    body: "Drag the chart's window or pick a date range to view earlier activity.",
};

const SINCE_LAST_VISIT_PRESETS = [
    {
        id: SINCE_LAST_VISIT_PRESET_ID,
        label: SINCE_LAST_VISIT_LABEL,
        value: {},
    },
];

export interface ActivityTimelineRange {
    /** The picked dates, or the days since the last visit; the list shows these. */
    window: DensityChartWindow;
    /** No date range is picked, so "Since your last visit" applies. */
    isLastVisit: boolean;
    /** The timeline's first day: history's, or with none known 30 days back or the window's start if earlier. */
    historyStart: string;
    /** What makes `historyStart` history's real start, so the zone before it can say so: a join date or the activity history limit. */
    historyStartedBy?: "join" | "limit";
}

const minDate = (first: string, second: string) =>
    first < second ? first : second;

const maxDate = (first: string, second: string) =>
    first > second ? first : second;

const toHistoryStartedBy = (joinDay: string) =>
    joinDay < ACTIVITY_HISTORY_MIN_DAY ? "limit" : "join";

/**
 * The window and where the timeline starts: the join date, or the activity
 * history limit when that's later. A picked date range is the
 * window, open ends reaching history's start and today, or the picked day
 * when it's beyond them. Without one, "Since
 * your last visit" is the days from the visit, or from history's start if
 * that's later, to today.
 */
export function resolveTimelineRange({
    dateRange,
    lastVisitSince,
    today,
    historyStartAt,
}: {
    dateRange: DateOnlyDateRange;
    lastVisitSince: Date;
    today: string;
    /** When history starts, e.g. the workspace's creation. */
    historyStartAt?: Date;
}): ActivityTimelineRange {
    const isLastVisit = !dateRange.start && !dateRange.end;
    const joinDay = historyStartAt ? toIsoDate(historyStartAt) : undefined;
    const historyDay =
        joinDay === undefined
            ? undefined
            : minDate(maxDate(joinDay, ACTIVITY_HISTORY_MIN_DAY), today);
    const fallbackStart = addCalendarDays(today, 1 - FALLBACK_HISTORY_DAYS);
    const visitDay = toIsoDate(lastVisitSince);
    const window = isLastVisit
        ? {
              start: minDate(maxDate(visitDay, historyDay ?? visitDay), today),
              end: today,
          }
        : {
              start:
                  dateRange.start ??
                  minDate(historyDay ?? fallbackStart, dateRange.end ?? today),
              end: dateRange.end ?? maxDate(dateRange.start ?? today, today),
          };
    return {
        window,
        isLastVisit,
        historyStart: historyDay ?? minDate(window.start, fallbackStart),
        historyStartedBy:
            joinDay === undefined ? undefined : toHistoryStartedBy(joinDay),
    };
}

/** The date filter's "Since your last visit" preset, shown on the trigger while it applies. */
export const toTimelineDateFilterProps = ({
    isLastVisit,
}: Pick<ActivityTimelineRange, "isLastVisit">): Pick<
    DateRangeFilterNextProps,
    "leadingPresets" | "requireSelection" | "selectedPresetId"
> => ({
    leadingPresets: SINCE_LAST_VISIT_PRESETS,
    requireSelection: true,
    ...(isLastVisit && { selectedPresetId: SINCE_LAST_VISIT_PRESET_ID }),
});
